<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/admin/mappers/new"
    create-label="Crear mapeador"
    create-icon="mdi-plus-box"
    remove-title="Eliminar mapeador"
  />
</template>

<script setup lang="ts">
import type { ThinMapper } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";

/**
 * Mapeadores de certificado: cómo se traducen los RDN de un X.509 a campos de
 * usuario al importar un p12.
 *
 * OJO: AdminCertificateMapperController exige ADMIN o
 * ADMIN_CERTIFICATES_MAPPERS, y SetupDataLoader no crea ese segundo rol. Sin
 * sembrarlo por fuera, sólo un ADMIN puro llega hasta acá.
 */
const view = useIndexView<ThinMapper, "id" | "name">({
  title: "Índice de mapeadores",
  rowKey: "id",
  fetchPage: (params) => banco.admin.mappers.find(params),
  remove: (item) => banco.admin.mappers.destroy(item.id),
  removedMessage: (item) => `Se eliminó el mapeador ID ${item.id}.`,
  filters: [
    { field: "id", label: "ID" },
    { field: "name", label: "Nombre" },
  ],
  confirm: {
    expected: (item) => String(item.id),
    label: "ID Mapeador",
    hint: "Para confirmar ingresa el <b>ID</b> del mapeador a eliminar.",
    error: "El ID de mapeador no es correcto.",
    warning: (item) =>
      `Esta acción eliminará el mapeador ${item.id}. Las importaciones de p12 que lo usen dejarán de funcionar.`,
  },
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "ID", value: "id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/mappers/{id}/edit" },
  },
  { header: { text: "Nombre", value: "name", class: "text-no-wrap" }, row: { type: "TEXT" } },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Editar", icon: "mdi-pencil", link: "/admin/mappers/{id}/edit" },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as ThinMapper),
        },
      ],
    },
  },
];
</script>
